<template>
  <IntercomLayout>
    <template #topbar>
      <IntercomTopbar
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="quotation-detail-view">
      <!-- Back link -->
      <button class="back-link" @click="router.push('/quotations')">
        &larr; Back to Quotations
      </button>

      <div v-if="isLoading" class="loading-state">Loading...</div>

      <div v-else-if="quotation" class="quotation-content">
        <!-- Header -->
        <div class="page-header">
          <div>
            <h1 class="page-title">{{ quotation.quotation_number }}</h1>
            <p class="page-subtitle">{{ quotation.customer_name || 'No customer' }}</p>
          </div>
          <div class="header-actions">
            <IntercomBadge :variant="getStatusVariant(displayStatus)">
              {{ capitalize(displayStatus) }}
            </IntercomBadge>
          </div>
        </div>

        <!-- Quotation Info -->
        <IntercomCard>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Customer</span>
              <span class="info-value">{{ quotation.customer_name || 'No customer' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Quotation Date</span>
              <span class="info-value">{{ formatDate(quotation.quotation_date) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Valid Until</span>
              <span class="info-value">
                {{ quotation.valid_until ? formatDate(quotation.valid_until) : 'No expiry' }}
                <IntercomBadge
                  v-if="isExpired"
                  variant="warning"
                  style="margin-left: 8px;"
                >Expired</IntercomBadge>
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Status</span>
              <IntercomBadge :variant="getStatusVariant(displayStatus)">
                {{ capitalize(displayStatus) }}
              </IntercomBadge>
            </div>
            <div v-if="quotation.customer_reference" class="info-item">
              <span class="info-label">Customer Reference</span>
              <span class="info-value">{{ quotation.customer_reference }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Total Amount</span>
              <span class="info-value highlight">{{ formatCurrency(quotation.total_amount) }}</span>
            </div>
          </div>

          <!-- Converted Invoice Link -->
          <div v-if="quotation.status === 'converted' && quotation.converted_invoice_id" class="converted-link">
            <span class="info-label">Converted to Invoice:</span>
            <button class="invoice-link" @click="router.push(`/invoices/${quotation.converted_invoice_id}`)">
              View Invoice &rarr;
            </button>
          </div>
        </IntercomCard>

        <!-- Line Items -->
        <IntercomCard>
          <h3 class="section-heading">Line Items</h3>
          <IntercomTable :columns="itemColumns" :data="quotation.items || []">
            <template #cell-quantity="{ value }">
              {{ parseFloat(Number(value).toFixed(3)) }}
            </template>
            <template #cell-unit_price="{ value }">
              {{ formatCurrency(value) }}
            </template>
            <template #cell-taxable_amount="{ value }">
              {{ formatCurrency(value) }}
            </template>
            <template #cell-tax_amount="{ value }">
              {{ formatCurrency(value) }}
            </template>
            <template #cell-line_total="{ value }">
              <strong>{{ formatCurrency(value) }}</strong>
            </template>
          </IntercomTable>
        </IntercomCard>

        <!-- Tax Summary -->
        <IntercomCard v-if="quotation.total_tax_amount > 0">
          <h3 class="section-heading">Tax Summary</h3>
          <div class="tax-grid">
            <div class="tax-item">
              <span class="info-label">Subtotal (Taxable)</span>
              <span class="info-value">{{ formatCurrency(quotation.subtotal) }}</span>
            </div>
            <div v-if="quotation.cgst_amount > 0" class="tax-item">
              <span class="info-label">CGST</span>
              <span class="info-value cgst">{{ formatCurrency(quotation.cgst_amount) }}</span>
            </div>
            <div v-if="quotation.sgst_amount > 0" class="tax-item">
              <span class="info-label">SGST</span>
              <span class="info-value sgst">{{ formatCurrency(quotation.sgst_amount) }}</span>
            </div>
            <div v-if="quotation.igst_amount > 0" class="tax-item">
              <span class="info-label">IGST</span>
              <span class="info-value igst">{{ formatCurrency(quotation.igst_amount) }}</span>
            </div>
            <div class="tax-item total">
              <span class="info-label">Grand Total</span>
              <span class="info-value">{{ formatCurrency(quotation.total_amount) }}</span>
            </div>
          </div>
        </IntercomCard>

        <!-- Notes -->
        <IntercomCard v-if="quotation.notes">
          <h3 class="section-heading">Notes</h3>
          <p class="notes-text">{{ quotation.notes }}</p>
        </IntercomCard>

        <!-- Terms and Conditions -->
        <IntercomCard v-if="quotation.terms_and_conditions">
          <h3 class="section-heading">Terms and Conditions</h3>
          <p class="notes-text">{{ quotation.terms_and_conditions }}</p>
        </IntercomCard>

        <!-- Actions -->
        <div class="action-bar">
          <IntercomButton
            variant="secondary"
            @click="handlePrint"
          >
            Print
          </IntercomButton>
          <IntercomButton
            v-if="quotation.status === 'draft'"
            variant="secondary"
            @click="showEditModal = true"
          >
            Edit Quotation
          </IntercomButton>
          <IntercomButton
            v-if="quotation.status === 'draft'"
            variant="secondary"
            @click="handleAccept"
          >
            Accept
          </IntercomButton>
          <IntercomButton
            v-if="quotation.status === 'draft'"
            variant="secondary"
            @click="handleReject"
          >
            Reject
          </IntercomButton>
          <IntercomButton
            v-if="canConvert"
            variant="primary"
            @click="handleConvert"
          >
            Convert to Invoice
          </IntercomButton>
          <IntercomButton
            v-if="quotation.status === 'draft'"
            variant="secondary"
            @click="handleDelete"
          >
            Delete
          </IntercomButton>
        </div>
      </div>

      <div v-else class="empty-state">Quotation not found</div>

      <!-- Edit Quotation Modal -->
      <QuotationForm
        v-model:show="showEditModal"
        :quotation="quotation"
        @success="handleEditSuccess"
      />

      <!-- Print Quotation Modal -->
      <IntercomModal
        v-model:show="showPrintModal"
        title="Print Quotation"
        width="860px"
      >
        <div v-if="isPrintLoading" class="print-state">Preparing quotation print...</div>
        <div v-else-if="printError" class="print-state print-error">{{ printError }}</div>
        <div v-else-if="printQuotationData" class="quotation-print-preview" :class="{ 'preview-a5': paperSize === 'A5' }">
          <QuotationPrint
            :quotation-data="printQuotationData"
            :business-data="printBusinessData || {}"
            :customer-data="printCustomerData || {}"
            :bank-accounts="printBankAccounts"
            :paper-size="paperSize"
          />
        </div>

        <template #footer>
          <div class="print-modal-actions">
            <IntercomButton variant="secondary" @click="closePrintModal">
              Close
            </IntercomButton>
            <IntercomButton
              variant="primary"
              :disabled="isPrintLoading || !printQuotationData"
              @click="printQuotation"
            >
              Print
            </IntercomButton>
          </div>
        </template>
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@shared/composables/useAuth.js'
import { useConfirm } from '@shared/composables/useConfirm.js'
import { useToast } from '@shared/composables/useToast.js'
import { useBilling } from '../composables/useBilling.js'
import { useQuotationPrint } from '../composables/useQuotationPrint.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomCard,
  IntercomTable,
  IntercomButton,
  IntercomBadge,
  IntercomModal,
} from '@shared/components/intercom'
import QuotationForm from '../components/QuotationForm.vue'
import QuotationPrint from '../components/QuotationPrint.vue'
import { billingNavSections } from '../config/navigation.js'
import { formatDate } from '../utils/billingUtils.js'

const route = useRoute()
const router = useRouter()
const { user, logout } = useAuth()
const { confirm } = useConfirm()
const toast = useToast()
const { getQuotation, acceptQuotation, rejectQuotation, convertQuotation, deleteQuotation, businessName, fetchBusinessName } = useBilling()

const navSections = billingNavSections

const quotation = ref(null)
const isLoading = ref(true)
const showEditModal = ref(false)
const {
  showPrintModal,
  isPrintLoading,
  printError,
  printQuotationData,
  printBusinessData,
  printCustomerData,
  printBankAccounts,
  paperSize,
  openQuotationPrint,
  closePrintModal,
  printQuotation,
} = useQuotationPrint()

const itemColumns = [
  { key: 'product_name', title: 'Product', width: '200px' },
  { key: 'hsn_code', title: 'HSN', width: '100px' },
  { key: 'quantity', title: 'Qty', align: 'right', width: '80px' },
  { key: 'unit_price', title: 'Unit Price', align: 'right', width: '120px' },
  { key: 'taxable_amount', title: 'Taxable', align: 'right', width: '120px' },
  { key: 'tax_amount', title: 'Tax', align: 'right', width: '100px' },
  { key: 'line_total', title: 'Total', align: 'right', width: '120px' },
]

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : ''

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val || 0)

/**
 * Check if quotation has expired (valid_until < today)
 */
const isExpired = computed(() => {
  if (!quotation.value?.valid_until) return false
  if (quotation.value.status !== 'draft') return false
  const validDate = new Date(quotation.value.valid_until)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return validDate < today
})

/**
 * Display status — shows 'expired' when applicable
 */
const displayStatus = computed(() => {
  if (isExpired.value) return 'expired'
  return quotation.value?.status || ''
})

/**
 * Whether convert action is available
 */
const canConvert = computed(() => {
  const s = quotation.value?.status
  return s === 'draft' || s === 'accepted'
})

const getStatusVariant = (s) => {
  switch (s) {
    case 'accepted':
      return 'success'
    case 'rejected':
      return 'error'
    case 'expired':
      return 'warning'
    case 'converted':
      return 'success'
    case 'draft':
    default:
      return 'default'
  }
}

const fetchQuotation = async () => {
  isLoading.value = true
  try {
    const response = await getQuotation(route.params.id)
    quotation.value = response.quotation || response
  } catch (error) {
    console.error('Failed to fetch quotation:', error)
    quotation.value = null
  } finally {
    isLoading.value = false
  }
}

const handleAccept = async () => {
  const ok = await confirm(`Accept quotation "${quotation.value.quotation_number}"?`, { title: 'Accept Quotation' })
  if (!ok) return
  try {
    await acceptQuotation(quotation.value.id)
    toast.success('Quotation accepted')
    await fetchQuotation()
  } catch (error) {
    console.error('Failed to accept quotation:', error)
    toast.error('Failed to accept quotation')
  }
}

const handleReject = async () => {
  const ok = await confirm(`Reject quotation "${quotation.value.quotation_number}"?`, { title: 'Reject Quotation' })
  if (!ok) return
  try {
    await rejectQuotation(quotation.value.id)
    toast.success('Quotation rejected')
    await fetchQuotation()
  } catch (error) {
    console.error('Failed to reject quotation:', error)
    toast.error('Failed to reject quotation')
  }
}

const handleConvert = async () => {
  const ok = await confirm(`Convert this quotation to a draft invoice?`, { title: 'Convert to Invoice' })
  if (!ok) return
  try {
    const result = await convertQuotation(quotation.value.id)
    const invoiceId = result.invoice?.id || result.data?.invoice?.id
    toast.success('Quotation converted to invoice')
    if (invoiceId) {
      router.push(`/invoices/${invoiceId}`)
    } else {
      await fetchQuotation()
    }
  } catch (error) {
    console.error('Failed to convert quotation:', error)
    toast.error('Failed to convert quotation: ' + (error.message || 'Unknown error'))
  }
}

const handleDelete = async () => {
  const ok = await confirm(`Delete quotation "${quotation.value.quotation_number}"? This cannot be undone.`, { title: 'Delete Quotation' })
  if (!ok) return
  try {
    await deleteQuotation(quotation.value.id)
    toast.success('Quotation deleted')
    router.push('/quotations')
  } catch (error) {
    console.error('Failed to delete quotation:', error)
    toast.error('Failed to delete quotation')
  }
}

const handleEditSuccess = async () => {
  showEditModal.value = false
  await fetchQuotation()
}

const handlePrint = async () => {
  await openQuotationPrint(quotation.value)
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusinessName()
  fetchQuotation()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.quotation-detail-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-2) 0;
  margin-bottom: var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-blue);
  background: none;
  border: none;
  cursor: pointer;
}

.back-link:hover {
  text-decoration: underline;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin: var(--space-1) 0 0;
}

.header-actions {
  display: flex;
  gap: var(--space-2);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--space-4);
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.info-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.info-value.highlight {
  color: var(--color-blue);
  font-size: var(--font-size-lg);
}

.info-value.cgst { color: #2563eb; }
.info-value.sgst { color: #7c3aed; }
.info-value.igst { color: #0891b2; }

.converted-link {
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.invoice-link {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-blue);
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
}

.invoice-link:hover {
  color: #0043cc;
}

.section-heading {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tax-grid {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  max-width: 350px;
}

.tax-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tax-item.total {
  padding-top: var(--space-3);
  border-top: 2px solid var(--color-gray-300);
}

.tax-item.total .info-value {
  font-size: var(--font-size-lg);
  color: var(--color-blue);
  font-weight: var(--font-weight-bold);
}

.notes-text {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

.action-bar {
  display: flex;
  gap: var(--space-3);
  margin-top: var(--space-6);
}

.print-state {
  padding: var(--space-8);
  text-align: center;
  color: var(--color-gray-500);
}

.print-error {
  color: #d03050;
}

.print-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  width: 100%;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: var(--space-10);
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
}

.quotation-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

@media (max-width: 768px) {
  .quotation-detail-view { padding: var(--space-4); }
  .page-header { flex-direction: column; gap: var(--space-3); }
  .info-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>
